import { useCallback, useEffect, useState } from 'react';
import { type ConsentDocStatus, getConsentStatus, postConsentAccept } from './api';
import type { ConsentGatePhase } from './consentGateState';

interface ConsentGateState {
  /** null while the initial GET is in flight or hasn't been requested yet
   *  (enabled=false) — the SAME "unknown, not empty" discipline
   *  useOnboardingGate's own corpus read already establishes: never coerced
   *  to an empty/needs-nothing array before server truth actually answers. */
  statuses: ConsentDocStatus[] | null;
  phase: ConsentGatePhase;
  error: string | null;
}

/**
 * Wires the ConsentGate screen to GET/POST /auth/consents. Mirrors
 * useOnboardingGate.ts's own shape (enabled-gated read, refresh callback) —
 * the read only fires once a real session exists (App.tsx gates `enabled` on
 * auth.user != null, same as useOnboardingGate), so a pre-signin stranger
 * never issues a GET /auth/consents that would just 401.
 */
export function useConsentGate(enabled: boolean) {
  const [state, setState] = useState<ConsentGateState>({
    statuses: null,
    phase: 'idle',
    error: null,
  });

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const result = await getConsentStatus();
      setState((s) => ({ ...s, statuses: result.statuses, phase: 'idle', error: null }));
    } catch (err) {
      // A failed read is genuinely "unknown," never "nothing needed" — never
      // silently wave a user past the consent gate because of a transient
      // network blip (the same discipline useOnboardingGate's own corpus
      // read failure-handling already establishes).
      setState((s) => ({ ...s, error: err instanceof Error ? err.message : String(err) }));
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  /** Accept every doc currently NOT 'current' — both docs are always
   *  presented and accepted together in this flow (see ConsentGate.tsx's own
   *  header comment for why a single combined accept, not per-doc). Posts
   *  sequentially rather than in parallel so a failure partway through
   *  leaves an honest, inspectable state (statuses re-read after) rather
   *  than a race between two concurrent inserts. */
  const acceptAll = useCallback(async () => {
    if (!state.statuses) return;
    setState((s) => ({ ...s, phase: 'accepting', error: null }));
    try {
      const pending = state.statuses.filter((s) => s.state !== 'current');
      for (const status of pending) {
        await postConsentAccept(status.doc, status.currentVersion);
      }
      const result = await getConsentStatus();
      setState({ statuses: result.statuses, phase: 'done', error: null });
    } catch (err) {
      setState((s) => ({
        ...s,
        phase: 'error',
        error: err instanceof Error ? err.message : String(err),
      }));
    }
  }, [state.statuses]);

  return { ...state, refresh, acceptAll };
}
